import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * Pinned photos carousel — polaroid stack with native scroll-snap.
 *
 * Why native scroll-snap + Framer Motion (vs. Swiper / a bespoke drag rig):
 *   • Real 60fps momentum on iOS, handled by the browser (no JS scroll
 *     listener in the critical path).
 *   • `scroll-snap-align: center` gives us perfect centre-stop physics
 *     for free, matching references like the "story list" carousel
 *     (enzomanuelmangano/animate-with-reanimated) without gesture
 *     handling code to maintain.
 *   • Framer Motion is still used for the *delight*: the active card
 *     slightly lifts and grows, idle cards recede. This mirrors the
 *     parallax/wiggle references while staying performant.
 *
 * Each polaroid reuses the gallery's aesthetic (white frame, 20px
 * inner radius, deterministic tilt) so the home page reads as the
 * same visual language as `MemorialImages`.
 */

const hash = (str = '') => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
  return Math.abs(h);
};

// ±3.2° — same envelope as the gallery PhotoCard so the two screens
// feel like they shuffle from the same drawer.
const seededRotation = (id) => {
  const h = hash(String(id) + 'pin');
  return ((h % 65) / 10) - 3.2;
};

const IMG_RADIUS = 20;
const FRAME_PADDING = 10;
const FRAME_RADIUS = IMG_RADIUS + FRAME_PADDING;

export default function PinnedPhotosCarousel({
  images = [],
  onSlideClick,
  initialIndex = 0,
}) {
  const scrollerRef = useRef(null);
  const count = images.length;
  const clampedInitial = Math.min(Math.max(initialIndex, 0), Math.max(count - 1, 0));
  const [activeIndex, setActiveIndex] = useState(clampedInitial);

  const recalcActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Only consider real slide elements. `el.children` also picks up
    // things like an inline <style> tag, which would shift every
    // index by one and break the pagination + active-card styling.
    const slides = el.querySelectorAll('[data-slide]');
    if (slides.length === 0) return;
    const rect = el.getBoundingClientRect();
    const center = rect.left + rect.width / 2;
    let closest = 0;
    let min = Infinity;
    slides.forEach((child, i) => {
      const r = child.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const d = Math.abs(cx - center);
      if (d < min) {
        min = d;
        closest = i;
      }
    });
    setActiveIndex(closest);
  }, []);

  // Jump to the requested initial slide synchronously *before* paint,
  // so the card is already centered on the first frame — no visible
  // "shift from first to second" flicker on load.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const slides = el.querySelectorAll('[data-slide]');
    const target = slides[clampedInitial];
    if (!target) return;
    const targetLeft = target.offsetLeft - (el.clientWidth - target.offsetWidth) / 2;
    el.scrollTo({ left: targetLeft, behavior: 'auto' });
    // Only on mount; subsequent slide changes come from user scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    recalcActive();
    const onScroll = () => recalcActive();
    el.addEventListener('scroll', onScroll, { passive: true });
    const onResize = () => recalcActive();
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [recalcActive, count]);

  const rotations = useMemo(
    () => images.map((img, i) => seededRotation(img.id || i)),
    [images]
  );

  if (count === 0) return null;

  const onKeyDown = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    const slides = el.querySelectorAll('[data-slide]');
    if (!slides[activeIndex]) return;
    if (e.key === 'ArrowRight' && activeIndex < count - 1) {
      e.preventDefault();
      slides[activeIndex + 1].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    if (e.key === 'ArrowLeft' && activeIndex > 0) {
      e.preventDefault();
      slides[activeIndex - 1].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  return (
    <div className="relative w-full select-none" style={{ touchAction: 'pan-x' }}>
      {/* Scoped to the wrapper (not the scroller) so it isn't counted
          as a scroller child when computing the active slide. */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      <div
        ref={scrollerRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="flex items-center gap-4 overflow-x-auto overflow-y-hidden no-scrollbar focus:outline-none"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          paddingLeft: 'calc(50% - 138px)',
          paddingRight: 'calc(50% - 138px)',
          paddingTop: 40,
          // Room for shadow; pagination sits just below with a slight
          // negative overlap so the dots read tight to the cards.
          paddingBottom: 48,
        }}
      >
        {images.map((img, i) => {
          const isActive = i === activeIndex;
          const rotation = rotations[i];
          return (
            <motion.button
              type="button"
              key={img.id || i}
              data-slide
              onClick={() => onSlideClick && onSlideClick(img, i)}
              initial={false}
              animate={{
                scale: isActive ? 1 : 0.86,
                y: isActive ? 0 : 10,
                rotate: isActive ? rotation : rotation * 0.6,
              }}
              transition={{ type: 'spring', stiffness: 240, damping: 26, mass: 0.7 }}
              whileTap={{ scale: isActive ? 0.97 : 0.86 }}
              className="relative shrink-0 focus:outline-none"
              style={{
                scrollSnapAlign: 'center',
                scrollSnapStop: 'always',
                width: 276,
                transformOrigin: 'center center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <div
                className="relative bg-white overflow-hidden"
                style={{
                  padding: FRAME_PADDING,
                  borderRadius: FRAME_RADIUS,
                  // Three stacked layers — a tight contact shadow, a
                  // medium ambient shadow, and a very-soft long tail —
                  // so the shadow fades out gradually instead of
                  // terminating in a hard line.
                  boxShadow: isActive
                    ? '0 1px 2px rgba(64, 42, 20, 0.08), ' +
                      '0 10px 22px rgba(64, 42, 20, 0.14), ' +
                      '0 28px 56px rgba(64, 42, 20, 0.10)'
                    : '0 1px 2px rgba(64, 42, 20, 0.06), ' +
                      '0 8px 18px rgba(64, 42, 20, 0.10), ' +
                      '0 22px 44px rgba(64, 42, 20, 0.07)',
                }}
              >
                <img
                  src={img.url}
                  alt=""
                  draggable={false}
                  loading={i < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                  style={{
                    display: 'block',
                    width: '100%',
                    aspectRatio: '4 / 5',
                    objectFit: 'cover',
                    borderRadius: IMG_RADIUS,
                  }}
                />
              </div>
            </motion.button>
          );
        })}
      </div>

      {count > 1 && (
        <div
          className="flex items-center justify-center gap-2 mb-2"
          style={{ marginTop: -28 }}
        >
          {images.map((_, i) => {
            const isActive = i === activeIndex;
            return (
              <motion.span
                key={i}
                aria-hidden
                animate={{
                  width: isActive ? 22 : 6,
                  backgroundColor: isActive ? '#44231E' : 'rgba(68, 35, 30, 0.22)',
                }}
                transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                style={{ height: 6, borderRadius: 999, display: 'block' }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
